import * as d3 from 'd3';
import './d3-fanchart.css';
import { colors, closest, DURATION, LEGEND_ITEM_RADIUS, getSVGPosition } from '../utils';
import draghandle from './d3-draghandle';
import chartTooltip from './d3-tooltip-legend';

const timeFormat = d3.timeFormat('%d-%b-%Y');
const timeFormatReadable = d3.timeFormat('%d %b %Y');

export default function () {
  const margin = { top: 10, right: 100, bottom: 45, left: 60 };
  const colorLegend = [colors.red, colors.lightblue, colors.darkblue, colors.lightblue, colors.red];
  const colorPercentiles = [colors.red, colors.lightblue, colors.darkblue, colors.lightblue, colors.red]; // Different order because we always want to draw the median last, i.e. on top

  let width = 400;
  let height = 300;
  let svg;
  let t;
  let _data;
  let _selection;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let label;
  let displayPercentiles = true;
  let handle;
  let tooltip;
  let dragging = false;
  let selected;
  let assets;
  let dropdown;
  let radioPercentiles;
  let radioSimulations;

  const dispatch = d3.dispatch('selected');

  function chart(selection, isResize) {
    selection.each(function (data) {
      _selection = selection;
      _data = data;
      chartWidth = Math.max(width - margin.left - margin.right, 0);
      chartHeight = Math.max(height - margin.top - margin.bottom, 0);

      t = d3.transition().duration(isResize ? 0 : DURATION);

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();

        g.append('g')
          .attr('class', 'fan-chart__percentiles');

        createLegend();
        createRadioButtons();
        createDateDropdown();
        createFocus();
        createHandle();
        createTooltip();

        g.append('g')
          .attr('class', 'fan-chart__sims');

        createLabel();
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();
      drawHandle();
      drawSims();
      drawPercentiles();
      drawLegend();
      drawRadioButtons();
      drawDateDropdown();
      drawFocus();
      drawLabel();
    });
  }

  function createScales() {
    const paddingFraction = 0.1;
    x = d3.scaleTime().rangeRound([0, chartWidth]);
    y = d3.scaleLinear().rangeRound([chartHeight, 0]);

    const minY = d3.min([d3.min(_data.sims, function (d) { return d3.min(d.values, function (c) { return c.value; }); }), d3.min(_data.percentiles, function (d) { return d3.min(d.values, function (c) { return c.value; }); })]);
    const maxY = d3.max([d3.min(_data.sims, function (d) { return d3.max(d.values, function (c) { return c.value; }); }), d3.max(_data.percentiles, function (d) { return d3.max(d.values, function (c) { return c.value; }); })]);
    const widthY = maxY - minY;

    x.domain([d3.min(_data.sims, function (d) { return d3.min(d.values, function (c) { return c.date; }); }), d3.max(_data.sims, function (d) { return d3.max(d.values, function (c) { return c.date; }); })]);
    y.domain([minY - paddingFraction * widthY, maxY + paddingFraction * widthY]);
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'fan-chart__axis fan-chart__axis--y');

    const xAxis = svg.select('g')
      .append('g')
      .attr('class', 'fan-chart__axis fan-chart__axis--x');

    xAxis.append('line')
      .attr('class', 'baseline-halo');

    xAxis.append('line')
      .attr('class', 'baseline');
  }

  function createLabel() {
    label = svg.append('text')
      .attr('class', 'fan-chart__label')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Value');
  }

  function createHandle() {
    const dates = _data.sims[0].values.map(function (d) { return d.date; }).slice(1); // Exclude first date

    handle = draghandle().height(chartHeight)
      .extent([x(dates[0]), x(dates[dates.length - 1])])
      .on('dragstarted', function (d) {
        dragging = true;
      })
      .on('dragended', function dragended(d) {
        dragging = false;
        const date = x.invert(d);
        const dates = _data.sims[0].values.map(function (d) { return d.date; }).slice(1);

        const { curr } = closest(date, dates);

        dispatch.call('selected', chart, 'date', d3.timeFormat('%d-%b-%Y')(curr));
      });

    const handleContainer = svg.select('g');
    handleContainer.datum(x(d3.timeParse('%d-%b-%Y')(selected))).call(handle);
  }

  function createLegend() {
    svg.select('g')
      .append('g')
      .attr('class', 'fan-chart__legend')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'end');
  }

  function createRadioButtons() {
    const create = function (_class, _label, checked) {
      const radio = _selection.append('div')
        .attr('class', 'fan-chart__radio fan-chart__radio--' + _class)
        .style('position', 'absolute');

      radio.append('input')
        .attr('type', 'radio')
        .attr('name', 'fan-options')
        .property('checked', checked)
        .attr('value', _class);

      radio.append('label')
        .append('text')
        .style('font-size', 12)
        .style('font-weight', 'normal')
        .style('padding-left', '5px')
        .text(_label);

      return radio
    }

    radioPercentiles = create('percentiles', 'percentiles', displayPercentiles);
    radioSimulations = create('simulations', 'simulations', !displayPercentiles);
  }

  function createDateDropdown() {
    dropdown = _selection.append('div')
      .attr('class', 'fan-chart__date-selector')
      .style('position', 'absolute')

    dropdown.append('select')
      .attr('class', 'form-control')
      .on('change', function () {
        dispatch.call('selected', chart, 'date', this.value);
      })
      .selectAll('option')
      .data(_data.sims[0].values.map(function (d) { return { value: timeFormat(d.date), text: d3.timeFormat('%d %b %Y')(d.date) }; }).slice(1))
      .enter().append('option')
      .attr('value', function (d) { return d.value; })
      .text(function (d) { return d.text; });

    dropdown.select('select').node().value = selected;
  }

  function createFocus() {
    const format = d3.format('.1f');

    svg.select('g')
      .append('rect')
      .attr('class', 'fan-chart__focus-rect')
      .attr('fill', 'rgba(255, 255, 255, 0)')
      .on('mouseover', function () {
        svg.selectAll('.fan-chart__focus').style('display', displayPercentiles && !dragging ? 'block' : 'none');
        dragging ? tooltip.hide() : tooltip.show();
      })
      .on('mouseout', function () {
        svg.selectAll('.fan-chart__focus').style('display', 'none');
        tooltip.hide();
      })
      .on('mousemove', mousemove);

    const focusList = [];

    _data.percentiles.forEach(function (d) {
      const focus = svg.select('g')
        .append('g')
        .attr('class', 'fan-chart__focus')
        .attr('display', 'none')
        .attr('pointer-events', 'none');

      focus.append('circle')
        .attr('r', 4)
        .attr('fill', 'none')
        .attr('stroke', 'black');

      focusList.push(focus);
    });

    function mousemove() {
      dragging ? tooltip.hide() : tooltip.show();
      const pos = d3.mouse(this);

      const dates = _data.sims[0].values.map(function (d) { return d.date; }).slice(1);

      const { currIndex } = closest(x.invert(pos[0]), dates);

      focusList.forEach(function (f, index) {
        const val = _data.percentiles[index].values[currIndex + 1].value;
        f.attr('transform', 'translate(' + x(dates[currIndex]) + ',' + y(val) + ')');
      });

      const SVGPos = getSVGPosition(this);

      tooltip.update(
        { x: x(dates[currIndex]) + SVGPos[0], y: pos[1] + SVGPos[1] },
        _data.percentiles.map(function (d, i) { return { key: colorLegend[i], value: format(d.values[currIndex + 1].value) } }).reverse(),
        timeFormatReadable(dates[currIndex]));
    }
  }

  function createTooltip() {
    tooltip = chartTooltip();
    const tooltipContainer = d3.select('body')
      .append('div')
      .attr('class', 'fan-chart__tooltip');

    tooltipContainer.datum([]).call(tooltip);
  }

  function drawAxes() {
    const xAxis = svg.select('.fan-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .call(d3.axisBottom(x).tickFormat(d3.timeFormat('%Y')).tickSize(-chartHeight))
      .select('.domain').remove();

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    const baselineYOffset = 2;

    xAxis.select('.baseline-halo')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', baselineYOffset)
      .attr('y2', baselineYOffset)
      .attr('stroke', '#bbb')
      .attr('stroke-width', 6)
      .attr('stroke-linecap', 'round');

    xAxis.select('.baseline')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', baselineYOffset)
      .attr('y2', baselineYOffset)
      .attr('stroke', '#eee')
      .attr('stroke-width', 4)
      .attr('stroke-linecap', 'round');

    xAxis.selectAll('text')
      .attr('dx', '-14px')
      .attr('dy', '14px')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    const g = svg.select('.fan-chart__axis--y')
      .call(d3.axisLeft(y).tickSize(-chartWidth));

    g.select('.domain').remove(); // Created by d3.axisLeft

    g.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    g.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);
  }

  function drawSims() {
    const sim = svg.select('g.fan-chart__sims').selectAll('.fan-chart__sim')
      .data(_data.sims);

    const linearLine = d3.line()
      .curve(d3.curveLinear)
      .x(function (d) { return x(d.date); })
      .y(function (d) { return y(d.value); });

    const color = d3.scaleOrdinal(d3.schemeCategory10);

    const simOpacity = 0.3;

    sim.enter().append('path')
      .attr('class', 'fan-chart__sim')
      .attr('d', function (d) { return linearLine(d.values); })
      .attr('stroke', function (d, i) { return color(i); })
      .attr('stroke-width', 2)
      .attr('fill', 'none')
      .attr('opacity', simOpacity)
      .on('mouseover', function (d) {
        d3.select(this).attr('stroke-width', 6).attr('opacity', 1);
      })
      .on('mouseout', function (d) {
        d3.select(this).attr('stroke-width', 2).attr('opacity', simOpacity);
      })
      .merge(sim)
      .attr('display', displayPercentiles ? 'none' : 'block')
      .transition(t)
      .attr('d', function (d) { return linearLine(d.values); });
  }

  function drawPercentiles() {
    const percentile = svg.select('g.fan-chart__percentiles').selectAll('.fan-chart__percentile')
      .data(_data.percentiles);

    const basisLine = d3.line()
      .curve(d3.curveBasis)
      .x(function (d) { return x(d.date); })
      .y(function (d) { return y(d.value); });

    percentile.enter().append('path')
      .attr('class', function (d) { return 'fan-chart__percentile fan-chart__percentile--' + d.percentile; })
      .attr('d', function (d) { return basisLine(d.values); })
      .attr('fill', 'none')
      .attr('stroke-width', '2')
      .attr('stroke', function (d, i) { return colorPercentiles[i]; })
      .merge(percentile)
      .attr('display', displayPercentiles ? 'block' : 'none')
      .transition(t)
      .attr('d', function (d) { return basisLine(d.values); });
  }

  function drawHandle() {
    const dates = _data.sims[0].values.map(function (d) { return d.date; }).slice(1); // Exclude first date

    handle.height(chartHeight)
      .extent([x(dates[0]), x(dates[dates.length - 1])]);

    const handleContainer = svg.select('g');
    handleContainer.datum(x(d3.timeParse('%d-%b-%Y')(selected))).call(handle);
  }

  function drawLegend() {
    const data = ['95th', '75th', 'median', '25th', '5th'];

    const legend = svg.select('.fan-chart__legend').selectAll('g')
      .data(data);

    const legendEnter = legend.enter().append('g')
      .attr('transform', function (d, i) { return 'translate(0,' + i * 16 + ')'; });

    legendEnter.append('circle')
      .attr('cx', 10)
      .attr('cy', 9)
      .attr('r', LEGEND_ITEM_RADIUS)
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .merge(legend.select('circle'))
      .attr('cx', chartWidth + 86)
      .attr('fill', function (d, i) { return colorLegend[i]; });

    legendEnter.append('text')
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .attr('fill', 'black')
      .merge(legend.select('text'))
      .attr('x', chartWidth + 75)
      .text(function (d) { return d; });
  }

  function drawFocus() {
    svg.select('.fan-chart__focus-rect')
      .attr('width', chartWidth)
      .attr('height', chartHeight);
  }

  function drawRadioButtons() {
    const onChange = function () {
      displayPercentiles = this === undefined ? true : (this.value === 'percentiles');

      svg.selectAll('.fan-chart__percentile')
        .style('display', displayPercentiles ? 'block' : 'none');

      svg.select('.fan-chart__legend')
        .style('display', displayPercentiles ? 'block' : 'none');

      svg.selectAll('.fan-chart__sim')
        .style('display', displayPercentiles ? 'none' : 'block');
    }

    radioPercentiles.select('input').on('change', onChange);
    radioSimulations.select('input').on('change', onChange);

    radioPercentiles.style('left', chartWidth + 80 + 'px')
      .style('top', margin.top + 150 + 'px');

    radioSimulations.style('left', chartWidth + 80 + 'px')
      .style('top', margin.top + 130 + 'px');
  }

  function drawDateDropdown() {
    // TODO: Extract magic constants
    dropdown.style('left', chartWidth + 80 + 'px')
      .style('top', margin.top + 103 + 'px');

    dropdown.select('select')
      .node().value = selected;
  }

  function drawLabel() {
    label.attr('transform', 'translate(10, ' + chartHeight / 2 + ')rotate(-90)');
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.assets = function (value) {
    if (!arguments.length) return assets;
    assets = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  chart.cleanup = function () {
    d3.select('.fan-chart__tooltip').remove();
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-fanchart.js